import {
  Color,
  DirectionalLight,
  HemisphereLight,
  SRGBColorSpace,
  type Texture,
  TextureLoader,
  type Fog,
  CylinderGeometry,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  PointLight,
  Scene,
  TorusGeometry,
} from 'three';
import type { Assets } from './assets';
import { heroModel, type CharacterModel } from './character';
import { Environment } from './environment';
import type { HeroLook } from './run-scene';
import type { StageScene } from './stage';
import type { Grade } from './stage';
import { artImage } from '../ui/ability-icons';
import { CAMP_PROPS, HERO_MODELS, ZONE_LOOKS } from './visuals';

/** Obóz: bohater na podeście wśród namiotów i pochodni, obracany myszą. Tu widać każdy założony przedmiot. */
export class HubScene implements StageScene {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(32, 1, 0.1, 200);
  readonly grade: Grade = ZONE_LOOKS.oboz!.grade;
  readonly rim = ZONE_LOOKS.oboz!.rim;
  /** Scena z klocków — tylko gdy nie ma malowanego tła obozu. */
  private readonly env: Environment | null = null;
  private backdrop: Texture | null = null;
  private hero: CharacterModel | null = null;
  private visual = '';
  private readonly rimLight = new PointLight('#ffffff', 0, 8);
  private readonly torches: PointLight[] = [];
  private yaw = 0.35;
  private dragging = false;
  private lastX = 0;
  private time = 0;

  constructor(
    private readonly assets: Assets,
    canvas: HTMLCanvasElement,
  ) {
    // Malowane tło (npm run asset:icons → art:hub) — wtedy w 3D zostaje tylko podest z bohaterem.
    const painted = artImage('hub');
    if (painted) {
      new TextureLoader().load(painted, (tex) => {
        tex.colorSpace = SRGBColorSpace;
        this.backdrop = tex;
        this.scene.background = tex;
        this.coverBackdrop();
      });
      this.scene.add(new HemisphereLight('#ffe2bd', '#2a1d14', 1.6));
      const key = new DirectionalLight('#ffd2a0', 2.6);
      key.position.set(-3, 6, 5);
      key.castShadow = true;
      key.shadow.mapSize.set(1024, 1024);
      // Przesunięcie wzdłuż normalnych: bez niego bohater zacieniał sam siebie w poziome paski (trądzik cienia).
      key.shadow.normalBias = 0.04;
      key.shadow.bias = -0.0005;
      this.scene.add(key);
    } else {
      const env = new Environment(this.scene, ZONE_LOOKS.oboz!, { decor: true });
      env.follow(0, 0);
      this.env = env;
    }

    // Podest: gładki kamienny krąg ze złotą obręczą.
    const plinth = new Mesh(
      new CylinderGeometry(1.55, 1.75, 0.3, 48),
      new MeshStandardMaterial({ color: '#6f675a', roughness: 0.85 }),
    );
    plinth.position.y = 0.15;
    plinth.receiveShadow = plinth.castShadow = true;
    const ring = new Mesh(
      new TorusGeometry(1.56, 0.035, 12, 96),
      new MeshStandardMaterial({ color: '#d9a84a', metalness: 0.9, roughness: 0.3, emissive: '#3a2408' }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.3;
    this.scene.add(ring);
    this.rimLight.position.set(0, 2.4, -1.6);
    this.scene.add(plinth, this.rimLight);

    for (const p of painted ? [] : CAMP_PROPS) {
      const obj = assets.template(p.path).scene.clone();
      obj.position.set(p.x, 0, p.z);
      obj.rotation.y = MathUtils.degToRad(p.rot);
      obj.scale.setScalar(p.scale);
      this.scene.add(obj);
      // Pochodnie naprawdę świecą — ciepłe, migoczące światło.
      if (p.path.includes('torch')) {
        const light = new PointLight('#ff9a3c', 8, 7, 1.6);
        light.position.set(p.x, 1.4, p.z);
        this.scene.add(light);
        this.torches.push(light);
      }
    }

    canvas.addEventListener('pointerdown', (e) => {
      this.dragging = true;
      this.lastX = e.clientX;
    });
    window.addEventListener('pointerup', () => (this.dragging = false));
    window.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      this.yaw += (e.clientX - this.lastX) * 0.01;
      this.lastX = e.clientX;
    });
  }

  setHero(look: HeroLook): void {
    // Ciało zależy też od rasy i płci (kolor skóry, proporcje) — wtedy nowy model.
    const key = `${look.visual}|${JSON.stringify(look.body ?? null)}`;
    if (key !== this.visual || !this.hero) {
      this.hero?.dispose();
      this.visual = key;
      this.hero = heroModel(this.assets, HERO_MODELS[look.visual]!, false, look.body);
      this.hero.root.position.y = 0.3;
      this.hero.setLoop(look.twoHanded ? '2H_Melee_Idle' : 'Idle');
      this.scene.add(this.hero.root);
    }
    this.hero.setEquipment(this.assets, look.equipment);
    // Z bronią do walki wręcz postawa gotowości (ostrze w górze); po założeniu broni, bo od niej zależy.
    const idle = this.hero.idleLoop;
    this.hero.setLoop(idle !== 'Idle' ? idle : look.twoHanded && this.hero.has('2H_Melee_Idle') ? '2H_Melee_Idle' : 'Idle');
    this.hero.setArmor(this.assets, look.armor);
    this.hero.dimAttachments(0.72);
    this.hero.setAura(look.aura.color, look.aura.strength);
    this.rimLight.color = new Color(look.aura.color ?? '#ffffff');
    this.rimLight.intensity = look.aura.color ? 6 + look.aura.strength * 20 : 2;
  }

  cheer(): void {
    this.hero?.playOnce('Cheer');
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    // Odległość tak dobrana, żeby w poziomie zawsze mieściło się ok. 6 jednostek (bohater + podest).
    const narrow = width <= 900;
    const halfFov = MathUtils.degToRad(this.camera.fov / 2);
    // Na szerokim ekranie bohater stoi w środkowym oknie między kolumnami menu — kamera trochę dalej.
    const distance = MathUtils.clamp((narrow ? 2.8 : 4.6) / (Math.tan(halfFov) * this.camera.aspect), narrow ? 6.4 : 8.6, 18);
    // Na wąskim ekranie panele są pod spodem — bohater ląduje w górnej części kadru.
    const lookY = narrow ? 1.35 - distance * Math.tan(halfFov) * 0.42 : 1.35;
    this.camera.position.set(0, 2.5 + distance * 0.08, distance);
    this.camera.lookAt(0, lookY, 0);
    // Mgła liczona od kamery: tło znika w oddali, ale bohater zostaje wyraźny.
    const fog = this.scene.fog as Fog | null;
    if (fog) {
      fog.near = distance + 3;
      fog.far = distance + 24;
    }
    this.camera.updateProjectionMatrix();
    this.coverBackdrop();
  }

  /** Malowane tło wypełnia ekran jak CSS „cover” (bez rozciągania obrazu). */
  private coverBackdrop(): void {
    const tex = this.backdrop;
    const img = tex?.image as { width: number; height: number } | undefined;
    if (!tex || !img) return;
    const screen = this.camera.aspect;
    const image = img.width / img.height;
    tex.matrixAutoUpdate = true;
    if (screen > image) {
      tex.repeat.set(1, image / screen);
      tex.offset.set(0, (1 - image / screen) / 2);
    } else {
      tex.repeat.set(screen / image, 1);
      tex.offset.set((1 - screen / image) / 2, 0);
    }
  }

  update(dt: number): void {
    this.time += dt;
    this.env?.update(dt);
    this.torches.forEach((t, i) => (t.intensity = 7 + Math.sin(this.time * 9 + i * 2) * 1.2 + Math.sin(this.time * 23 + i) * 0.8));
    if (!this.hero) return;
    if (!this.dragging) this.yaw += (0.35 - this.yaw) * Math.min(1, dt * 0.4);
    this.hero.root.rotation.y = this.yaw;
    this.hero.update(dt);
  }
}
